import React, { useState, useEffect } from 'react';
import {
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Typography,
  Box,
  Divider,
  Button,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  Tooltip
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import DescriptionIcon from '@mui/icons-material/Description';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CommentIcon from '@mui/icons-material/Comment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { generateClient } from 'aws-amplify/api';
import { loggedInUserState } from 'redux/userSlice';
import { organizationState } from 'redux/organizationSlice';
import { getNavigationPath, useNavPath, NAVIGATED_FROM } from 'constants/routes';
import { 
  getNotificationsByUser, 
  getUnreadNotificationsCount,
  markNotificationAsRead,
  archiveNotification 
} from 'graphql/notifications';
import './NotificationBell.scss';

const API = generateClient();

const NotificationBell = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [markingAsRead, setMarkingAsRead] = useState({});
  const navigate = useNavigate();
  const { loggedInUser } = useSelector(loggedInUserState);
  const { selectedClient, userOrgType } = useSelector(organizationState);
  const NAV_PATH = useNavPath(userOrgType);
  const open = Boolean(anchorEl);

  // Fetch unread count
  const fetchUnreadCount = async () => {
    if (!loggedInUser?.id) return;
    
    try {
      const result = await API.graphql({
        query: getUnreadNotificationsCount,
        variables: {
          userID: loggedInUser.id
        }
      });
      const unreadNotifications = result.data?.notificationsByUser?.items || [];
      setUnreadCount(unreadNotifications.length);
    } catch (error) {
      console.error('Error fetching unread notifications count:', error);
    }
  };

  // Fetch notifications
  const fetchNotifications = async () => {
    if (!loggedInUser?.id) return;
    
    setLoading(true);
    try {
      const result = await API.graphql({
        query: getNotificationsByUser,
        variables: {
          userID: loggedInUser.id,
          sortDirection: 'DESC',
          limit: 20
        }
      });
      const items = result.data?.notificationsByUser?.items || [];
      setNotifications(items);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  // Mark notification as read
  const handleMarkAsRead = async (notification) => {
    if (notification.status === 'READ') return;
    
    setMarkingAsRead(prev => ({ ...prev, [notification.id]: true }));
    try {
      await API.graphql({
        query: markNotificationAsRead,
        variables: {
          id: notification.id,
          readAt: new Date().toISOString()
        }
      });
      
      // Update local state
      setNotifications(prev => 
        prev.map(n => 
          n.id === notification.id 
            ? { ...n, status: 'READ', readAt: new Date().toISOString() }
            : n
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    } finally {
      setMarkingAsRead(prev => ({ ...prev, [notification.id]: false }));
    }
  };

  // Handle notification click
  const handleNotificationClick = async (notification) => {
    await handleMarkAsRead(notification);
    
    // Navigate to relevant page based on notification type
    if (notification.type === 'DOCUMENT_UPLOAD_RECEIVED' && notification.documentRequestID) {
      // Navigate to Email Pulse with document request
      navigate(`/dashboard/email-activity?tab=pulse&request=${notification.documentRequestID}`);
    } else if (notification.certificateID) {
      // Use proper routing structure for certificate navigation
      const certificatePath = getNavigationPath(NAV_PATH.CLIENT_CERTIFICATE_DETAILS, {
        clientId: notification.clientID || selectedClient?.id,
        vendorId: notification.vendorID || NAVIGATED_FROM.CERTIFICATES,
        certificateId: notification.certificateID
      });
      navigate(certificatePath);
    } else if (notification.vendorID) {
      // Navigate to vendor details if no certificate specified
      const vendorPath = getNavigationPath(NAV_PATH.CLIENT_VENDOR_DETAILS, {
        clientId: notification.clientID || selectedClient?.id,
        vendorId: notification.vendorID
      });
      navigate(vendorPath);
    }
    
    handleClose();
  };

  // Mark all as read
  const handleMarkAllAsRead = async () => {
    const unreadNotifications = notifications.filter(n => n.status === 'UNREAD');
    
    for (const notification of unreadNotifications) {
      await handleMarkAsRead(notification);
    }
  };

  // Get icon based on notification type
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'CERTIFICATE_SUBMISSION':
        return <DescriptionIcon fontSize="small" color="primary" />;
      case 'CERTIFICATE_SCAN_COMPLETE':
        return <AssignmentTurnedInIcon fontSize="small" color="info" />;
      case 'CERTIFICATE_EXPIRING':
        return <WarningAmberIcon fontSize="small" color="warning" />;
      case 'COMMENT_MENTION':
        return <CommentIcon fontSize="small" color="secondary" />;
      case 'CERTIFICATE_APPROVED':
        return <CheckCircleIcon fontSize="small" color="success" />;
      case 'CERTIFICATE_REJECTED':
        return <CancelIcon fontSize="small" color="error" />;
      case 'DOCUMENT_UPLOAD_RECEIVED':
        return <UploadFileIcon fontSize="small" color="success" />;
      default:
        return <NotificationsNoneIcon fontSize="small" />;
    }
  };

  // Format time ago
  const formatTimeAgo = (date) => {
    const now = new Date();
    const notificationDate = new Date(date);
    const diffMs = now - notificationDate;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    
    return notificationDate.toLocaleDateString();
  };

  // Handle menu open
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    fetchNotifications();
  };

  // Handle menu close
  const handleClose = () => {
    setAnchorEl(null);
  };

  // Poll for new notifications
  useEffect(() => {
    fetchUnreadCount();
    
    // Poll every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);
    
    return () => clearInterval(interval);
  }, [loggedInUser?.id]);

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton
          onClick={handleClick}
          size="large"
          aria-label={`${unreadCount} new notifications`}
          color="inherit"
          sx={{ ml: 1 }}
        >
          <Badge badgeContent={unreadCount} color="error">
            {unreadCount > 0 ? <NotificationsIcon /> : <NotificationsNoneIcon />}
          </Badge>
        </IconButton>
      </Tooltip>
      
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          elevation: 6,
          sx: {
            width: 400,
            maxHeight: 500,
            overflow: 'visible',
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ p: 2, pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Notifications
          </Typography>
          {unreadCount > 0 && (
            <Button 
              size="small" 
              onClick={handleMarkAllAsRead}
              sx={{ mt: 0.5 }}
            >
              Mark all as read
            </Button>
          )}
        </Box>
        
        <Divider />
        
        <Box sx={{ maxHeight: 350, overflow: 'auto' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : notifications.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <NotificationsNoneIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
              <Typography color="text.secondary">
                No notifications yet
              </Typography>
            </Box>
          ) : (
            notifications.map((notification) => (
              <MenuItem
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                sx={{
                  py: 1.5,
                  px: 2,
                  backgroundColor: notification.status === 'UNREAD' ? 'action.hover' : 'transparent',
                  '&:hover': {
                    backgroundColor: 'action.selected',
                  },
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
                disabled={markingAsRead[notification.id]}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  {getNotificationIcon(notification.type)}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant="body2" sx={{ fontWeight: notification.status === 'UNREAD' ? 600 : 400 }}>
                      {notification.title}
                    </Typography>
                  }
                  secondary={
                    <>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        {notification.message}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatTimeAgo(notification.createdAt)}
                      </Typography>
                    </>
                  }
                />
              </MenuItem>
            ))
          )}
        </Box>
        
        {notifications.length > 0 && (
          <>
            <Divider />
            <Box sx={{ p: 1 }}>
              <Button
                fullWidth
                size="small"
                onClick={() => {
                  navigate('/notifications');
                  handleClose();
                }}
              >
                View all notifications
              </Button>
            </Box>
          </>
        )}
      </Menu>
    </>
  );
};

export default NotificationBell;